import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useId,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRoutineSetupChecklist } from '../contexts/RoutineSetupChecklistContext';
import { useToast } from '../contexts/ToastContext';
import { useMood } from '../theme/MoodContext';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import Icon from '../components/Icon/Icon';
import ModalDialog from '../components/Dialog/ModalDialog';
import { guestDataService } from '../services/guestDataService';
import sunIllustration from '../assets/sun.png';
import bedIllustration from '../assets/bed.png';
import postExerciseIllustration from '../assets/postexercise.png';
import backFromWorkIllustration from '../assets/backfromwork.png';
import beachDayIllustration from '../assets/beachday.png';
import afterNightOutIllustration from '../assets/afternightout.png';
import cabinetIllustration from '../assets/empty_cabinet.png';
import './HomePage.css';

const ADDABLE_ROUTINES = [
  {
    id: 'post-exercise',
    name: 'Post-Exercise',
    description: 'Cool down after you move',
    icon: 'fitness',
    illustration: 'post-exercise',
  },
  {
    id: 'back-from-work',
    name: 'Back from Work',
    description: 'Reset after the workday',
    icon: 'work',
    illustration: 'back-from-work',
  },
  {
    id: 'beach-day',
    name: 'Beach Day',
    description: 'Sun, salt, and recovery',
    icon: 'sun',
    illustration: 'beach-day',
  },
  {
    id: 'after-night-out',
    name: 'After a Night Out',
    description: 'Gentle late-night recovery',
    icon: 'moon',
    illustration: 'after-night-out',
  },
];

const ILLUSTRATIONS = {
  sun: sunIllustration,
  bed: bedIllustration,
  'post-exercise': postExerciseIllustration,
  'back-from-work': backFromWorkIllustration,
  'beach-day': beachDayIllustration,
  'after-night-out': afterNightOutIllustration,
  cabinet: cabinetIllustration,
};

const MODE_COPY = {
  morning: {
    mode: 'morning',
    label: 'Morning',
    shortLabel: 'AM',
    headline: 'Morning ritual',
    body: 'Ease into the day with a few intentional steps.',
    cta: 'Open morning routine',
    path: '/routines/morning',
    illustration: 'sun',
    kind: 'routine',
  },
  night: {
    mode: 'night',
    label: 'Night',
    shortLabel: 'PM',
    headline: 'Night ritual',
    body: 'Slow down and reset your skin before bed.',
    cta: 'Open night routine',
    path: '/routines/night',
    illustration: 'bed',
    kind: 'routine',
  },
  'post-exercise': {
    mode: 'active',
    label: 'Post-Exercise',
    shortLabel: 'Move',
    headline: 'After you move',
    body: 'Cool down, cleanse, and recover after a workout.',
    cta: 'Open this routine',
    path: '/routines/custom/post-exercise',
    illustration: 'post-exercise',
    kind: 'routine',
  },
  'back-from-work': {
    mode: 'focus',
    label: 'Back from Work',
    shortLabel: 'Work',
    headline: 'After work',
    body: 'Shake off the day and give your skin a reset.',
    cta: 'Open this routine',
    path: '/routines/custom/back-from-work',
    illustration: 'back-from-work',
    kind: 'routine',
  },
  'beach-day': {
    mode: 'sun',
    label: 'Beach Day',
    shortLabel: 'Beach',
    headline: 'Beach day care',
    body: 'Protect, soothe, and restore after sun and salt.',
    cta: 'Open this routine',
    path: '/routines/custom/beach-day',
    illustration: 'beach-day',
    kind: 'routine',
  },
  'after-night-out': {
    mode: 'nightout',
    label: 'After a Night Out',
    shortLabel: 'Out',
    headline: 'After a night out',
    body: 'Gentle recovery for late nights and makeup.',
    cta: 'Open this routine',
    path: '/routines/custom/after-night-out',
    illustration: 'after-night-out',
    kind: 'routine',
  },
  cabinet: {
    mode: 'cabinet',
    label: 'Skincare Cabinet',
    shortLabel: 'Cabinet',
    headline: 'Your cabinet',
    body: 'Browse every product you own.',
    cta: 'Open cabinet',
    path: '/skincarecabinet',
    illustration: 'cabinet',
    kind: 'link',
  },
  add: {
    mode: 'add',
    label: 'Add routine',
    shortLabel: 'Add',
    headline: 'Add a moment',
    body: 'Create a ritual for after work, workouts, beach days, and more.',
    cta: 'Create routine',
    path: null,
    illustration: null,
    kind: 'action',
  },
};

// Pointer travel (px) that separates a tap on an orb from a drag of the dial
const DRAG_THRESHOLD = 6;

// Matches .orbit-dial-slot width so the outer orbs sit on the track edges.
const DIAL_SLOT = 84;

function wrapIndex(index, length) {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshProgress } = useRoutineSetupChecklist();
  const { showToast } = useToast();
  const reducedMotion = usePrefersReducedMotion();
  const dialLabelId = useId();

  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);
  const [routineToRemove, setRoutineToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [customRoutines, setCustomRoutines] = useState([]);
  const [selectedId, setSelectedId] = useState('night');
  const [dialWidth, setDialWidth] = useState(680);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [now] = useState(() => new Date());

  const selectedIdRef = useRef(selectedId);
  const dragOffsetRef = useRef(0);
  const velocityRef = useRef(0);
  const lastPointerRef = useRef({ x: 0, t: 0 });
  const pointerStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const orbitRef = useRef(null);
  const dialRef = useRef(null);
  const dialItemsRef = useRef(null);
  const addOrbRef = useRef(null);
  const addPopoverRef = useRef(null);
  const preferredAppliedRef = useRef(false);

  const routines = useMemo(() => {
    const morning = {
      id: 'morning',
      ...MODE_COPY.morning,
      deletable: false,
    };
    const night = {
      id: 'night',
      ...MODE_COPY.night,
      deletable: false,
    };
    const cabinet = {
      id: 'cabinet',
      ...MODE_COPY.cabinet,
      deletable: false,
    };
    const add = {
      id: 'add',
      ...MODE_COPY.add,
      deletable: false,
    };

    const customs = (customRoutines || []).map((routine) => {
      const preset = MODE_COPY[routine.routine_type] || {
        mode: 'custom',
        label: routine.routine_name,
        shortLabel: routine.routine_name?.split(' ')[0] || 'Custom',
        headline: routine.routine_name,
        body: 'A custom ritual for this moment in your day.',
        cta: 'Open this routine',
        path: `/routines/custom/${routine.routine_type}`,
        illustration: routine.routine_type,
        kind: 'routine',
      };
      return {
        id: routine.routine_type,
        ...preset,
        label: routine.routine_name || preset.label,
        headline: routine.routine_name || preset.headline,
        path: `/routines/custom/${routine.routine_type}`,
        deletable: true,
        routineType: routine.routine_type,
        routineName: routine.routine_name,
      };
    });

    // Day flow, then shelf + create: morning → customs → night → cabinet → add
    return [morning, ...customs, night, cabinet, add];
  }, [customRoutines]);

  const activateItem = useCallback(
    (item) => {
      if (!item) return;
      if (item.kind === 'action' || item.id === 'add') {
        setIsAddPopoverOpen((open) => !open);
        return;
      }
      setIsAddPopoverOpen(false);
      if (item.path) navigate(item.path);
    },
    [navigate]
  );

  const availableAddRoutines = useMemo(() => {
    const owned = new Set((customRoutines || []).map((r) => r.routine_type));
    return ADDABLE_ROUTINES.filter((routine) => !owned.has(routine.id));
  }, [customRoutines]);

  const count = routines.length;
  const activeIndex = Math.max(0, routines.findIndex((r) => r.id === selectedId));
  const active = routines[activeIndex] || routines[0];

  useMood(active?.mode);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    dragOffsetRef.current = dragOffset;
  }, [dragOffset]);

  const fetchCustomRoutines = useCallback(async () => {
    try {
      const list = await guestDataService.getCustomRoutines(user);
      setCustomRoutines(list);
    } catch (error) {
      console.error('Error fetching custom routines:', error);
      setCustomRoutines([]);
    }
  }, [user]);

  useEffect(() => {
    fetchCustomRoutines();
  }, [fetchCustomRoutines]);

  useEffect(() => {
    if (count === 0 || preferredAppliedRef.current) return;
    const preferredId = now.getHours() >= 17 || now.getHours() < 5 ? 'night' : 'morning';
    setSelectedId(preferredId);
    preferredAppliedRef.current = true;
  }, [count, now]);

  useEffect(() => {
    if (selectedId !== 'add') setIsAddPopoverOpen(false);
  }, [selectedId]);

  useEffect(() => {
    if (!isAddPopoverOpen) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (addPopoverRef.current?.contains(target)) return;
      if (addOrbRef.current?.contains(target)) return;
      setIsAddPopoverOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsAddPopoverOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isAddPopoverOpen]);

  // Keep the popover above the add orb and nudge horizontally so it stays on-screen.
  useEffect(() => {
    if (!isAddPopoverOpen || active?.id !== 'add') return undefined;

    const popover = addPopoverRef.current;
    if (!popover) return undefined;

    const margin = 16;

    const adjust = () => {
      const rect = popover.getBoundingClientRect();
      if (rect.width === 0) return;

      let shift = 0;
      if (rect.left < margin) {
        shift = margin - rect.left;
      } else if (rect.right > window.innerWidth - margin) {
        shift = window.innerWidth - margin - rect.right;
      }

      popover.style.setProperty('--popover-shift-x', `${shift}px`);
    };

    adjust();
    const frame = requestAnimationFrame(adjust);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(adjust) : null;
    resizeObserver?.observe(popover);

    window.addEventListener('resize', adjust);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', adjust);
      popover.style.removeProperty('--popover-shift-x');
    };
  }, [isAddPopoverOpen, active?.id, activeIndex, dialWidth, availableAddRoutines.length]);

  useEffect(() => {
    const node = dialItemsRef.current;
    if (!node) return undefined;

    const update = () => setDialWidth(node.clientWidth || 680);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [user]);

  const snapTo = useCallback(
    (nextIndex) => {
      if (count === 0) return;
      const routine = routines[wrapIndex(nextIndex, count)];
      if (routine) {
        setSelectedId(routine.id);
        if (routine.id !== 'add') setIsAddPopoverOpen(false);
      }
      setDragOffset(0);
      velocityRef.current = 0;
    },
    [count, routines]
  );

  const nudge = useCallback(
    (direction) => {
      const currentIdx = Math.max(0, routines.findIndex((r) => r.id === selectedIdRef.current));
      snapTo(currentIdx + direction);
    },
    [snapTo, routines]
  );

  // Wheel / trackpad rotation
  useEffect(() => {
    const node = orbitRef.current;
    if (!node || count < 2) return undefined;

    let wheelAccum = 0;
    let wheelTimer = null;

    const onWheel = (event) => {
      // Prefer vertical intent; fall back to horizontal trackpad pans
      const dominant =
        Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (Math.abs(dominant) < 1) return;
      event.preventDefault();
      wheelAccum += dominant;
      if (Math.abs(wheelAccum) > 40) {
        nudge(wheelAccum > 0 ? 1 : -1);
        wheelAccum = 0;
      }
      window.clearTimeout(wheelTimer);
      wheelTimer = window.setTimeout(() => {
        wheelAccum = 0;
      }, 140);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', onWheel);
      window.clearTimeout(wheelTimer);
    };
  }, [count, nudge]);

  const endDrag = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const velocity = velocityRef.current;
    const offset = dragOffsetRef.current;
    const currentIdx = Math.max(0, routines.findIndex((r) => r.id === selectedIdRef.current));
    let next = currentIdx;

    if (Math.abs(velocity) > 0.45) {
      next += velocity > 0 ? 1 : -1;
    } else if (Math.abs(offset) > 0.28) {
      next += offset > 0 ? 1 : -1;
    }

    snapTo(next);
  }, [isDragging, snapTo, routines]);

  const onPointerDown = (event) => {
    if (count < 2) return;
    // A mouse has the dial to grab; touch gets the whole shell so the stage
    // swipes like a carousel.
    if (event.pointerType === 'mouse' && !dialRef.current?.contains(event.target)) return;

    // Capture is deferred until the pointer actually travels: capturing here
    // retargets pointerup to the shell and swallows clicks on the orbs.
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      node: event.currentTarget,
    };
    velocityRef.current = 0;
    lastPointerRef.current = { x: event.clientX, t: event.timeStamp };
  };

  const onPointerMove = (event) => {
    const start = pointerStartRef.current;
    if (!start || count < 2) return;

    if (!isDraggingRef.current) {
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      // A mostly vertical gesture belongs to the scroller, not the dial.
      if (Math.abs(dy) > Math.abs(dx)) {
        pointerStartRef.current = null;
        return;
      }
      isDraggingRef.current = true;
      setIsDragging(true);
      try {
        start.node.setPointerCapture?.(event.pointerId);
      } catch {
        // Capture is an optimisation; the gesture still tracks without it.
      }
      lastPointerRef.current = { x: event.clientX, t: event.timeStamp };
      return;
    }

    const { x, t } = lastPointerRef.current;
    const dx = event.clientX - x;
    const dt = Math.max(16, event.timeStamp - t);
    // Dragging right reveals previous (negative offset toward previous)
    const nextOffset = dragOffsetRef.current - dx / 220;
    dragOffsetRef.current = nextOffset;
    setDragOffset(nextOffset);
    velocityRef.current = (-dx / dt) * 16;
    lastPointerRef.current = { x: event.clientX, t: event.timeStamp };
  };

  const onPointerEnd = () => {
    pointerStartRef.current = null;
    isDraggingRef.current = false;
    endDrag();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nudge(1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nudge(-1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activateItem(active);
    }
  };

  const handleCreateRoutine = async (routineType) => {
    try {
      const result = await guestDataService.createCustomRoutine(user, {
        routineType: routineType.id,
        routineName: routineType.name,
        description: routineType.description,
        icon: routineType.icon,
      });
      if (result) {
        showToast(`${routineType.name} routine created`);
        setIsAddPopoverOpen(false);
        await fetchCustomRoutines();
        setSelectedId(routineType.id);
        window.dispatchEvent(new Event('customRoutineChanged'));
        refreshProgress();
      } else {
        throw new Error('Failed to create routine');
      }
    } catch (error) {
      console.error('Error creating routine:', error);
      showToast('Failed to create routine. Please try again.', 'error');
    }
  };

  const handleRemoveRoutine = async () => {
    if (!routineToRemove || isRemoving) return;
    setIsRemoving(true);
    try {
      const removed = await guestDataService.deleteCustomRoutine(
        user,
        routineToRemove.routineType
      );
      if (!removed) throw new Error('Failed to remove routine');

      showToast(`${routineToRemove.label} removed`);
      if (selectedIdRef.current === routineToRemove.id) setSelectedId('morning');
      setRoutineToRemove(null);
      await fetchCustomRoutines();
      window.dispatchEvent(new Event('customRoutineChanged'));
      refreshProgress();
    } catch (error) {
      console.error('Error removing routine:', error);
      showToast('Failed to remove routine. Please try again.', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  // Resting layout is even across the track so adding or removing a moment
  // reflows the remaining orbs instead of leaving a wrap-around hole.
  const usable = Math.max(0, dialWidth - DIAL_SLOT);
  const step = count > 1 ? usable / (count - 1) : 0;

  return (
    <div
      className={`home-page orbit-page ${isDragging ? 'is-dragging' : ''} ${
        reducedMotion ? 'orbit-page--reduced' : ''
      } ${isAddPopoverOpen ? 'orbit-page--popover-open' : ''}`}
    >
      <section
        className="orbit-shell"
        ref={orbitRef}
        tabIndex={0}
        role="region"
        aria-labelledby={dialLabelId}
        aria-roledescription="rotary selector"
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
          <div className="orbit-stage">
            <div key={active?.id} className="orbit-stage-panel">
              <div className="orbit-stage-copy">
                <span className="orbit-mode-pill">{active?.shortLabel}</span>
                <Typography variant="h2" className="orbit-stage-title" id={dialLabelId}>
                  {active?.headline}
                </Typography>
                <Typography variant="body" className="orbit-stage-body">
                  {active?.body}
                </Typography>
                <div className="orbit-stage-actions">
                  <Button variant="primary" onClick={() => activateItem(active)}>
                    {active?.cta}
                  </Button>
                </div>
              </div>

              <button
                type="button"
                className={`orbit-stage-object ${active?.id === 'add' ? 'orbit-stage-object--add' : ''}`}
                onClick={() => activateItem(active)}
                aria-label={active?.cta}
              >
                <span className="orbit-stage-halo" />
                {active?.id === 'add' ? (
                  <span className="orbit-stage-add" aria-hidden>
                    <Icon name="add" size="large" />
                  </span>
                ) : (
                  <img
                    src={ILLUSTRATIONS[active?.illustration] || sunIllustration}
                    alt=""
                    className={`orbit-stage-image orbit-stage-image--${active?.illustration}`}
                    draggable={false}
                  />
                )}
              </button>
            </div>
          </div>

          <div
            className={`orbit-dial ${isAddPopoverOpen ? 'orbit-dial--popover-open' : ''}`}
            ref={dialRef}
          >
            <div className="orbit-dial-glow" aria-hidden />

            <div
              className="orbit-dial-items"
              ref={dialItemsRef}
              role="listbox"
              aria-label="Day moments"
              aria-activedescendant={active?.id ? `orbit-item-${active.id}` : undefined}
            >
              {routines.map((routine, index) => {
                const x = count > 1 ? -usable / 2 + index * step : 0;
                const arcT = Math.min(1, Math.abs(x) / Math.max(usable / 2, 1));
                const y = arcT * arcT * 40;
                const isActive = index === activeIndex && Math.abs(dragOffset) < 0.35;
                const scale = isActive ? 1 : 0.84;
                const opacity = isActive ? 1 : 0.78;
                const z = isActive ? 40 : 20;
                const isAdd = routine.id === 'add';

                return (
                  <div
                    key={routine.id}
                    className={`orbit-dial-slot${isAdd ? ' orbit-dial-slot--add' : ''}${
                      isAdd && isAddPopoverOpen ? ' orbit-dial-slot--popover-open' : ''
                    }`}
                    style={{
                      transform: `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${scale})`,
                      opacity,
                      zIndex: isAdd && isAddPopoverOpen ? 60 : z,
                    }}
                  >
                    <button
                      id={`orbit-item-${routine.id}`}
                      ref={isAdd ? addOrbRef : undefined}
                      type="button"
                      role="option"
                      className={`orbit-dial-item ${isActive ? 'is-active' : ''} ${
                        isAdd ? 'orbit-dial-item--add' : ''
                      } ${isAdd && isAddPopoverOpen ? 'is-popover-open' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        // Clicking selects a moment; opening it is a second
                        // action on the stage or the already-active orb.
                        if (isActive) {
                          activateItem(routine);
                          return;
                        }
                        snapTo(index);
                      }}
                      aria-selected={isActive}
                      aria-expanded={isAdd ? isAddPopoverOpen : undefined}
                      aria-haspopup={isAdd ? 'dialog' : undefined}
                      aria-label={isActive ? routine.cta || routine.label : routine.label}
                    >
                      <span className={`orbit-dial-orb ${isAdd ? 'orbit-dial-orb--add' : ''}`}>
                        {isAdd ? (
                          <Icon name="add" size="large" />
                        ) : (
                          <img
                            src={ILLUSTRATIONS[routine.illustration] || sunIllustration}
                            alt=""
                            draggable={false}
                          />
                        )}
                      </span>
                      <span className="orbit-dial-label">{routine.shortLabel}</span>
                    </button>

                    {isAdd && isAddPopoverOpen && (
                      <div
                        className="orbit-add-popover"
                        ref={addPopoverRef}
                        role="dialog"
                        aria-label="Add a routine"
                        onPointerDown={(event) => event.stopPropagation()}
                      >
                        <Typography variant="body-small-bold" className="orbit-add-popover-title">
                          Add a moment
                        </Typography>
                        {availableAddRoutines.length === 0 ? (
                          <Typography variant="body-small" className="orbit-add-popover-empty">
                            You already added every moment.
                          </Typography>
                        ) : (
                          <div className="orbit-add-popover-grid">
                            {availableAddRoutines.map((addRoutine) => (
                              <button
                                key={addRoutine.id}
                                type="button"
                                className="orbit-add-widget"
                                onClick={() => handleCreateRoutine(addRoutine)}
                              >
                                <span className="orbit-add-widget-art">
                                  <img
                                    src={ILLUSTRATIONS[addRoutine.illustration]}
                                    alt=""
                                    draggable={false}
                                  />
                                </span>
                                <span className="orbit-add-widget-copy">
                                  <span className="orbit-add-widget-name">{addRoutine.name}</span>
                                  <span className="orbit-add-widget-desc">
                                    {addRoutine.description}
                                  </span>
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {routine.deletable && (
                      <button
                        type="button"
                        className="orbit-dial-remove"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsAddPopoverOpen(false);
                          setRoutineToRemove(routine);
                        }}
                        aria-label={`Remove ${routine.label} routine`}
                      >
                        <Icon name="close" size="small" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      <ModalDialog
        isOpen={Boolean(routineToRemove)}
        onClose={() => setRoutineToRemove(null)}
        title={`Remove ${routineToRemove?.label || 'routine'}?`}
        maxWidth="420px"
        secondaryButton={{ label: 'Cancel', onClick: () => setRoutineToRemove(null) }}
        primaryButton={{
          label: isRemoving ? 'Removing…' : 'Remove',
          onClick: handleRemoveRoutine,
          disabled: isRemoving,
        }}
      >
        <Typography variant="body">
          This removes the routine and everything you added to it. Your products stay in your
          cabinet.
        </Typography>
      </ModalDialog>
    </div>
  );
};

export default HomePage;
